// analytics — consent-gated client-side analytics loader.
//
// Currently supports Plausible (cookieless, GDPR-friendly default).
// Only initialises when:
//   1. VITE_PLAUSIBLE_DOMAIN is set in the build, AND
//   2. the user has explicitly granted "statistics" consent via the
//      cookie banner.
//
// Re-evaluates on the `fw-consent-changed` event so admin toggles +
// user revocations take effect within the same SPA session.

import { hasConsent } from '@/lib/cookieConsent';

const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;
const PLAUSIBLE_SCRIPT = 'https://plausible.io/js/script.outbound-links.js';

let scriptInjected = false;
let listenerAttached = false;

type PlausibleFn = (event: string, opts?: { props?: Record<string, unknown> }) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn & { q?: unknown[] };
  }
}

function injectPlausible(): void {
  if (scriptInjected) return;
  if (!PLAUSIBLE_DOMAIN) return;
  if (typeof document === 'undefined') return;
  const tag = document.createElement('script');
  tag.defer = true;
  tag.src = PLAUSIBLE_SCRIPT;
  tag.dataset.domain = PLAUSIBLE_DOMAIN;
  document.head.appendChild(tag);
  // Stub queue so event-firing before the script loads doesn't drop.
  window.plausible = window.plausible
    || (((...args: unknown[]) => {
      const q = (window.plausible as { q?: unknown[] }).q ??= [];
      q.push(args);
    }) as PlausibleFn);
  scriptInjected = true;
}

/**
 * Boot the analytics layer. Idempotent — safe to call from app root.
 * If the user revokes consent later, the script stays loaded but
 * trackEvent() will short-circuit.
 */
export function bootAnalytics(): void {
  if (hasConsent('statistics')) injectPlausible();
  if (!listenerAttached && typeof window !== 'undefined') {
    window.addEventListener('fw-consent-changed', () => {
      if (hasConsent('statistics')) injectPlausible();
    });
    listenerAttached = true;
  }
}

/**
 * Record a custom event. No-ops when no consent or no provider configured.
 * Safe to sprinkle in click-handlers without worrying about consent state.
 */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  if (!hasConsent('statistics')) return;
  if (typeof window === 'undefined') return;
  if (typeof window.plausible !== 'function') return;
  try {
    window.plausible(name, props ? { props } : undefined);
  } catch {
    // ignore — analytics must never break the app
  }
}

/** For tests. */
export function __resetAnalyticsForTest(): void {
  scriptInjected = false;
  listenerAttached = false;
  if (typeof window !== 'undefined') {
    delete (window as { plausible?: unknown }).plausible;
  }
}
